import React, { useEffect, useRef, useState } from 'react';
import { useAppActions, useAppState } from '../../../overmind';
import classNames from 'classnames';
import { FetchType } from '../../../enums';
import { Mode, PagePath } from '../../navigation.enums';
import { useNavigate } from 'react-router';
import { themedAssetUrl } from '../../../library/helpers/themed-asset-url';
import { BurgerIcon } from '../burger.icon';
import { AvatarIcon, RadioButton, useWindowResize } from '@keplerco/core';
import navStyles from '../../navigation.module.css';
import styles from './top-nav-menu.module.css';
import KeplerNavlink from '../../guards/kepler-navlink';

export function TopNavMenu(props: { isMobile: boolean; isTablet: boolean; onOpenSideNavDialog: () => void }): JSX.Element {
  const { isMobile, isTablet, onOpenSideNavDialog } = props;

  const navigate = useNavigate();

  const actions = useAppActions();
  const { user, forcedAssessments, mode } = useAppState();

  const [windowSize] = useWindowResize();

  const menuRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState<boolean>(false);

  function setMode(mode: Mode) {
    actions.setMode(mode);
    actions.setActiveCompanySlug(user?.companySlug);

    mode === Mode.UserDashboard ? navigate(PagePath.dashboard) : navigate(PagePath.companies);

    setOpen(false);
  }

  function onClickWindowHandler(event: MouseEvent): void {
    if (!open) return;
    if (!menuRef.current?.contains(event.target as any)) setOpen(false);
  }

  useEffect(() => {
    if (!open) return () => window.removeEventListener('click', onClickWindowHandler);

    window.requestIdleCallback(() => window.addEventListener('click', onClickWindowHandler));

    return () => window.removeEventListener('click', onClickWindowHandler);
  }, [open]);

  useEffect(() => {
    if (open) setOpen(false);
  }, [windowSize]);

  return (
    <React.Fragment>
      {!!user && (
        <React.Fragment>
          <button className={styles.button} onClick={() => (isMobile || isTablet ? onOpenSideNavDialog() : setOpen(true))}>
            <div className={navStyles.navAvatarIconContainer} style={{ width: 30, height: 30 }}>
              <AvatarIcon name={{ firstName: user?.firstName, lastName: user?.lastName }} />
            </div>

            <div className={styles.burger}>
              <BurgerIcon size={40} tone="text" />
            </div>
          </button>
        </React.Fragment>
      )}

      {open && (
        <div ref={menuRef} className={styles.menu}>
          {user?.isSystemAdmin && !forcedAssessments.length && (
            <React.Fragment>
              <span className={classNames(navStyles.navCaption, styles.title)}>Switch to:</span>

              <button className={classNames(navStyles.navCaption, styles.item, { [styles.isChecked]: mode === Mode.AdminDashboard })} onClick={() => setMode(Mode.AdminDashboard)}>
                <RadioButton id="AdminDashboard" checked={mode === Mode.AdminDashboard} onChange={() => setMode(Mode.AdminDashboard)} clickablearea="label" alignitems="center">
                  <span className={navStyles.navCaption}>Admin dashboard</span>
                </RadioButton>
              </button>

              <button className={classNames(navStyles.navCaption, styles.item, { [styles.isChecked]: mode === Mode.UserDashboard })} onClick={() => setMode(Mode.UserDashboard)}>
                <RadioButton id="UserDashboard" checked={mode === Mode.UserDashboard} onChange={() => setMode(Mode.UserDashboard)} clickablearea="label" alignitems="center">
                  <span className={navStyles.navCaption}>User dashboard</span>
                </RadioButton>
              </button>

              <hr className={navStyles.navDivider} />
            </React.Fragment>
          )}

          {!forcedAssessments.length && (
            <React.Fragment>
              <span className={classNames(navStyles.navCaption, styles.title)}>Your account:</span>

              <KeplerNavlink
                className={classNames(navStyles.navCaption, styles.item)}
                to={PagePath.profile}
                onClick={() => {
                  actions.setMode(Mode.UserDashboard);
                  setOpen(false);
                }}
              >
                <img className={navStyles.navIcon} src={themedAssetUrl('icons/sidebar/profile.icon.svg')} />
                <span className={navStyles.navCaption}>Profile</span>
              </KeplerNavlink>
            </React.Fragment>
          )}

          <a className={classNames(navStyles.navCaption, styles.item)} href="https://legal.keplerco.io" target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
            <img className={navStyles.navIcon} src={themedAssetUrl('icons/sidebar/legal.icon.svg')} />
            <span className={navStyles.navCaption}>Legal</span>
          </a>

          <button
            className={classNames(navStyles.navCaption, styles.item)}
            onClick={async () => {
              actions.startLoader({ path: PagePath.root, type: FetchType.PageFetching });
              setOpen(false);
              await actions.logout();
              actions.stopLoader(PagePath.root);
            }}
            style={{ gap: 0 }}
          >
            <img className={navStyles.navIcon} src={themedAssetUrl('icons/sidebar/signout.icon.svg')} />
            <span className={navStyles.navCaption}>Log out</span>
          </button>
        </div>
      )}
    </React.Fragment>
  );
}
